import React, { useEffect, useCallback, useState, memo } from "react";
import { Box, Grid2, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTruncatedName } from "../../../Helper/Formatters/TextFormatter";
import { getDigitalTwinBatteryTags, getDigitalTwinRealTimeMatrics } from "../../../Api/DigitalTwin";
import { getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { isNumber, normalizeKey } from "../../../Helper/Common/common_helpers";
import { useBatteryInfo } from "../../../Pages/DigitalTwinV3/DigitalTwinContext";
import DefaultInfo from "../../DigitalTwinV3/components/Header/DefaultInfo";
import CustomBusDeviceInfo from "./CustomBusDeviceInfo";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import { usePermissions } from "../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../Static/FeatureMapping";

const PageHeaderContainer = styled(Grid2)(() => ({
    padding: '16px 24px 20px 24px',
    borderRadius: '12px',
    border: '1px solid #E9E9E9',
    background: '#FFF',
    marginBottom: '8px',
}));

const MetricDisplayBox = memo(({ metric }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <Typography
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '16px' }}
                variant="fw500s12lh24cA2A2A2"
            >
                {getTruncatedName(t(metric.label), 60)}
            </Typography>
            <Typography
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '8px' }}
                variant="fw500s14lh24c444B51"
            >
                {metric.value}
            </Typography>
        </Box>
    );
});

const formatMetricValue = (metricData, key) => {
    const { value, unit = "" } = metricData;
    if (!value || ["NA", "na"].includes(value)) return "NA";
    if (key === "addedOn") return getMetricTimeStamp(value, "DD MMM YYYY hh:MM A");
    if (["sohEst", "eqCycleEst", "minSOHBMS", "odometer", "ext_trip_distance"].includes(key)) {
        const numeric = parseFloat(value);
        return isNaN(numeric) ? "NA" : `${numeric.toFixed(0)} ${unit}`;
    }
    if (["balanceState"].includes(key)) {
        const numeric = parseFloat(value);
        return isNaN(numeric) ? "NA" : `${numeric.toFixed(2)} ${unit}`;
    }
    return `${value} ${unit}`.trim();
};

const getSOCColors = (soc) => {
    if (!isNumber(soc) || soc === "NA") {
        return { soc_bg_color: '#FFEEE8', soc_text_color: '#FF4D00' };
    }
    const socValue = Number(soc);
    if (socValue < 30) return { soc_bg_color: '#FFE4E4', soc_text_color: '#2E2E2E' };
    if (socValue <= 50) return { soc_bg_color: '#FEF1C6', soc_text_color: '#2E2E2E' };
    return { soc_bg_color: '#EEF4DB', soc_text_color: '#2E2E2E' };
};

/**
 * Custom platform Digital Twin header.
 *
 * Differs from DigitalTwinV3/DigitalTwinHeader in three ways:
 *   1. No OperationalStatusAction (Operational/Non-Operational toggle).
 *   2. No EditVehicleDetailsDialog (uses CustomBusDeviceInfo for bus apps).
 *   3. Metric chips are filtered to the keys listed in
 *      metaInfo.customRealTimeMatrixes; the metric-selector dropdown is
 *      omitted since the set is fixed by org config.
 */
function CustomDigitalTwinHeader() {
    const { batteryID, batteryInfo, batteryConfig, showBusDeviceInfo } = useBatteryInfo();
    const pageContent = usePagesContent();
    const { t } = useTranslation(['common', 'digital_twin']);
    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const showBmsFaults = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS, 'view');

    const [isLoading, setIsLoading] = useState(true);
    const [batteryTags, setBatteryTags] = useState({ soc: {}, active_alerts: {}, active_diagnostics: {} });
    const [metricList, setMetricList] = useState([]);

    const customRealTimeMatrixes = pageContent?.metaInfo?.customRealTimeMatrixes || {};
    const allowedMetricKeys = Object.keys(customRealTimeMatrixes);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [tagsRes, metricsRes] = await Promise.all([
                getDigitalTwinBatteryTags(batteryID, { tags: ["soc", "active_alerts", "active_diagnostics"] }),
                getDigitalTwinRealTimeMatrics(batteryID, { embed: 'VEHICLE_INFO' }),
            ]);
            setBatteryTags(tagsRes.data);

            const allMetrics = Object.entries(metricsRes.data).map(([key, data]) => ({
                key,
                ...data,
                value: formatMetricValue(data, key),
                label: t(`digital_twin:live_metrics.${normalizeKey(data.label)}`, data.label),
            }));

            const filtered = allowedMetricKeys.length
                ? allMetrics.filter((m) => allowedMetricKeys.includes(m.key))
                : allMetrics.slice(0, 4);

            setMetricList(filtered);
        } catch (err) {
            console.error("CustomDigitalTwinHeader: failed to fetch header data", err);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, t, allowedMetricKeys.join(',')]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (isLoading) {
        return (
            <PageHeaderContainer container justifyContent="center" alignItems="center" sx={{ height: '154px' }}>
                <CartLoader />
            </PageHeaderContainer>
        );
    }

    return (
        <PageHeaderContainer container justifyContent="space-between">
            <Grid2 container flexDirection="column">
                {showBusDeviceInfo() ? (
                    <CustomBusDeviceInfo batteryInfo={batteryInfo} batteryTags={batteryTags} getSOCColors={getSOCColors} showBmsFaults={showBmsFaults} />
                ) : (
                    <DefaultInfo batteryInfo={batteryInfo} batteryTags={batteryTags} getSOCColors={getSOCColors} showBmsFaults={showBmsFaults} />
                )}
            </Grid2>

            <Grid2 container flexDirection="column" alignItems="flex-end" justifyContent="flex-end">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                    <Grid2 container gap={3.5}>
                        {metricList.map((metric, index) => (
                            <MetricDisplayBox key={index} metric={metric} />
                        ))}
                    </Grid2>
                </Box>
            </Grid2>
        </PageHeaderContainer>
    );
}

export default CustomDigitalTwinHeader;
